/** On the patient page: referrals, and recording what came of an open one. */
import {
  type ReferralOutcome,
  type ServiceRequest,
  closeReferral,
  referralOutcomeOf,
  transportOf,
  urgencyOf,
} from '@assist/core';
import { useState } from 'preact/hooks';
import { app, clock } from '../controller.js';
import { eth } from '../dates.js';
import { t } from '../i18n.js';

export function ReferralsCard(props: { referrals: ServiceRequest[]; onSaved: () => void }) {
  if (!props.referrals.length) return null;
  const list = [...props.referrals].sort((a, b) => b.authoredOn.localeCompare(a.authoredOn));
  return (
    <div class="card" data-card="referrals">
      <h3>{t.referrals}</h3>
      <ul class="plain">
        {list.map((sr) => (
          <Referral key={sr.id} sr={sr} onSaved={props.onSaved} />
        ))}
      </ul>
    </div>
  );
}

function Referral({ sr, onSaved }: { sr: ServiceRequest; onSaved: () => void }) {
  const [open, setOpen] = useState(false);
  const [outcome, setOutcome] = useState<ReferralOutcome | ''>('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const done = referralOutcomeOf(sr);
  const save = async () => {
    if (!outcome || !app.session) return;
    setBusy(true);
    try {
      await app.session.save(closeReferral(sr, outcome, clock.today(), note));
      app.saved();
      setOpen(false);
      onSaved();
    } finally {
      setBusy(false);
    }
  };
  return (
    <li data-referral={sr.id}>
      <div>
        <strong>{eth(sr.authoredOn)}</strong> {sr.code.text}{' '}
        {urgencyOf(sr) !== 'routine' ? (
          <span class="pill red">{t.urgencies[urgencyOf(sr)]}</span>
        ) : null}{' '}
        {sr.status === 'active' ? <span class="pill amber">{t.referralOpen}</span> : null}
      </div>
      <div class="muted">
        {sr.reasonCode?.[0]?.text}
        {transportOf(sr) ? ` · ${t.transport}: ${transportOf(sr)}` : ''}
      </div>
      {done ? (
        <div>
          {t.referralOutcomes[done.outcome]}
          {done.date ? ` · ${eth(done.date)}` : ''}
        </div>
      ) : null}
      {sr.note?.length ? <div class="muted">{sr.note.map((n) => n.text).join(' · ')}</div> : null}
      <a class="small sec" href={`#/patient/${sr.subject.reference.split('/')[1]}/letter/${sr.id}`}>
        {t.printLetter}
      </a>
      {sr.status === 'active' && !open ? (
        <button type="button" class="small" onClick={() => setOpen(true)}>
          {t.recordOutcome}
        </button>
      ) : null}
      {open ? (
        <div class="attempt-form">
          <fieldset class="tri wrapbtn" aria-label={t.outcome}>
            {(['completed', 'not_attended', 'died', 'cancelled'] as const).map((o) => (
              <button
                key={o}
                type="button"
                class={outcome === o ? 'on' : ''}
                aria-pressed={outcome === o}
                onClick={() => setOutcome(o)}
              >
                {t.referralOutcomes[o]}
              </button>
            ))}
          </fieldset>
          <div class="field">
            <label for={`rn-${sr.id}`}>{t.note}</label>
            <input
              id={`rn-${sr.id}`}
              value={note}
              onInput={(e) => setNote(e.currentTarget.value)}
            />
          </div>
          <div class="actions">
            <button type="button" class="ghost" onClick={() => setOpen(false)}>
              {t.cancel}
            </button>
            <button
              type="button"
              class="primary"
              disabled={!outcome || busy}
              onClick={() => void save()}
            >
              {t.save}
            </button>
          </div>
        </div>
      ) : null}
    </li>
  );
}
